import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useDocumentAutofill, type AutofillCardStatus, type AutofillFieldMark, type AutofillSource } from '../../hooks/useDocumentAutofill'
import type { AutofillTarget, AutofillValue } from '../../utils/documentAutofill/targets'
import { trackBoxTruckProfileDocumentAutofillAction, type BoxTruckDocumentScope } from '../../utils/analytics'

/** What a document card and a form field need from the form's autofill: a card reads its files, a field shows its state. */
interface DocumentAutofillContextValue {
    /** The card's document name, or undefined when the card is not read by AI. */
    labelOf(card: string): string | undefined
    /** The fields the card's documents can fill, by their form labels. */
    fieldsOf(card: string): readonly string[]
    statusOf(card: string): AutofillCardStatus | undefined
    /** Why a document can write nothing into this form now, when it can't (the fields are locked). */
    lockedMessage?: string
    /** Read the card's files that were not read yet. */
    start(card: string, sources: readonly AutofillSource[]): void
    /** Read again the card's files that failed, while they have retries left (Try again). */
    retry(card: string): void
    /** Stop the card's running read; what it filled before stays. */
    cancel(card: string): void
    /** A file left the card: what only it filled goes. */
    forget(card: string, sourceId: string): void
    /** HR took off the card a file read as another document ("Remove from this card"), for analytics. */
    removedWrongFile(card: string, sourceId: string): void
    fieldOf(key: string): AutofillFieldMark | undefined
    accept(key: string, value: AutofillValue): void
}

const DocumentAutofillContext = createContext<DocumentAutofillContextValue | null>(null)

/** The form's autofill, or null outside a form that reads documents (the card and fields then behave as before). */
export const useDocumentAutofillContext = (): DocumentAutofillContextValue | null => useContext(DocumentAutofillContext)

interface DocumentAutofillProviderProps<K extends string> {
    scope: BoxTruckDocumentScope
    targets: Partial<Record<string, AutofillTarget<K>>>
    valueOf: (key: K) => unknown
    apply: (values: Partial<Record<K, AutofillValue | null>>) => void
    locked?: ReadonlySet<K>
    hidden?: ReadonlySet<K>
    reveal?: (keys: K[]) => void
    /** Values filled from a document before the form opened (see `useDocumentAutofill`). */
    initialFilled?: { card: string; values: Partial<Record<K, AutofillValue>> }
    lockedMessage?: string
    children: ReactNode
}

/** One per profile form: wraps the form so its document cards fill its fields (see `useDocumentAutofill`). */
export const DocumentAutofillProvider = <K extends string>({
    scope,
    targets,
    valueOf,
    apply,
    locked,
    hidden,
    reveal,
    initialFilled,
    lockedMessage,
    children,
}: DocumentAutofillProviderProps<K>) => {
    const { cards, start, retry, cancel, forget, fieldState, accept } = useDocumentAutofill({
        scope,
        targets,
        valueOf,
        apply,
        locked,
        hidden,
        reveal,
        initialFilled,
    })
    const value = useMemo<DocumentAutofillContextValue>(
        () => ({
            labelOf: (card) => targets[card]?.label,
            fieldsOf: (card) => targets[card]?.fills ?? [],
            statusOf: (card) => cards[card],
            lockedMessage,
            start,
            retry,
            cancel,
            forget,
            removedWrongFile: (card, sourceId) => {
                const status = cards[card]
                const problem = status?.status === 'read' ? status.problems.find((candidate) => candidate.sourceId === sourceId) : undefined
                trackBoxTruckProfileDocumentAutofillAction(scope, card, 'remove_wrong_file', { readAs: problem?.readAs, cached: problem?.cached })
            },
            // A field asks by its own key; a key this form does not fill simply has no state.
            fieldOf: (key) => fieldState(key as K),
            accept: (key, accepted) => accept(key as K, accepted),
        }),
        [scope, targets, cards, lockedMessage, start, retry, cancel, forget, fieldState, accept]
    )
    return <DocumentAutofillContext.Provider value={value}>{children}</DocumentAutofillContext.Provider>
}
